import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import publicAdmissionsService from '../../services/publicAdmissionsService';

const emptyForm = {
  first_name: '',
  last_name: '',
  other_names: '',
  date_of_birth: '',
  gender: 'male',
  guardian_name: '',
  guardian_relationship: '',
  guardian_phone: '',
  guardian_email: '',
  notes: '',
  honeypot: '',
};

const DOCUMENT_TYPES = [
  { value: 'birth_certificate', label: 'Birth Certificate' },
  { value: 'previous_report_card', label: 'Previous Report Card' },
  { value: 'passport_photo', label: 'Passport Photo' },
  { value: 'other', label: 'Other' },
];

const MAX_DOCUMENTS = 5;

function ApplyPage() {
  const { schoolCode } = useParams();
  const [school, setSchool] = useState(null);
  const [schoolError, setSchoolError] = useState(false);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const [formData, setFormData] = useState(emptyForm);
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Payment step — only used when the school requires an application fee.
  const [payment, setPayment] = useState(null); // { url, reference, amount }
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | waiting | success | failed
  const paymentWindowRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    publicAdmissionsService.getSchoolInfo(schoolCode)
      .then(data => setSchool(data))
      .catch(() => setSchoolError(true))
      .finally(() => setLoadingSchool(false));
  }, [schoolCode]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
    };
  }, []);

  const addDocumentSlot = () => {
    if (documents.length >= MAX_DOCUMENTS) return;
    setDocuments((prev) => [...prev, { file: null, documentType: 'birth_certificate' }]);
  };

  const updateDocumentSlot = (index, patch) => {
    setDocuments((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const removeDocumentSlot = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadSelectedDocuments = async (applicantId) => {
    const withFiles = documents.filter((d) => d.file);
    if (withFiles.length === 0) return;
    try {
      await publicAdmissionsService.uploadDocuments(schoolCode, applicantId, withFiles);
    } catch (error) {
      toast.error(error.message || 'Application submitted, but some documents failed to upload. The school can request them separately.');
    }
  };

  const checkStatus = useCallback(async (reference) => {
    try {
      const result = await publicAdmissionsService.checkPaymentStatus(schoolCode, reference);
      if (result.status === 'success') {
        setPaymentStatus('success');
        setEmailSent(true);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      } else if (result.status === 'failed') {
        setPaymentStatus('failed');
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      }
    } catch (error) {
      // transient — keep polling
    }
  }, [schoolCode]);

  const startPaymentPolling = (reference) => {
    setPaymentStatus('waiting');
    checkStatus(reference);
    pollIntervalRef.current = setInterval(() => checkStatus(reference), 4000);
  };

  const openPaymentWindow = (url, reference) => {
    const win = window.open(url, 'admission_fee_payment', 'width=800,height=700');
    if (!win) {
      toast.error('Please enable pop-ups to complete the application fee payment, then use "Check payment status" below.');
      setPaymentStatus('waiting');
      checkStatus(reference);
      return;
    }
    paymentWindowRef.current = win;
    startPaymentPolling(reference);

    const closeWatcher = setInterval(() => {
      if (win.closed) {
        clearInterval(closeWatcher);
        checkStatus(reference);
      }
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await publicAdmissionsService.submitApplication(schoolCode, formData);
      await uploadSelectedDocuments(result.applicant_id);

      if (result.payment_required) {
        setPayment({ url: result.payment_url, reference: result.reference, amount: result.amount });
        setSubmitted(true);
        openPaymentWindow(result.payment_url, result.reference);
      } else {
        setEmailSent(!!formData.guardian_email);
        setSubmitted(true);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSchool) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (schoolError) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>School Not Found</h1>
            <p>We couldn't find a school with this application link. Please check the link and try again.</p>
          </div>
          <div className="auth-footer">
            <Link to="/">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    if (payment) {
      return (
        <div className="auth-page">
          <div className="auth-container">
            <div className="auth-header">
              <h1>
                {paymentStatus === 'success' ? 'Application Complete' : 'Complete Your Application Fee Payment'}
              </h1>
              {paymentStatus === 'success' && (
                <p>Your application fee of GHS {Number(payment.amount).toFixed(2)} was received. Thank you for applying to {school.name}{emailSent ? ' — a confirmation email is on its way.' : '.'}</p>
              )}
              {paymentStatus === 'waiting' && (
                <p>A payment window has opened for GHS {Number(payment.amount).toFixed(2)}. Complete the payment there — this page will update automatically once it's confirmed.</p>
              )}
              {paymentStatus === 'failed' && (
                <p>The payment didn't go through. You can try again below.</p>
              )}
              {paymentStatus === 'idle' && (
                <p>Your application was saved. Click below to pay the GHS {Number(payment.amount).toFixed(2)} application fee and finish submitting.</p>
              )}
            </div>
            {paymentStatus !== 'success' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-full"
                  onClick={() => openPaymentWindow(payment.url, payment.reference)}
                >
                  {paymentStatus === 'failed' ? 'Try Payment Again' : 'Open Payment Window'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-full"
                  onClick={() => checkStatus(payment.reference)}
                >
                  Check Payment Status
                </button>
              </div>
            )}
            <div className="auth-footer">
              <Link to="/">Back to home</Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>Application Submitted</h1>
            <p>
              Thank you for applying to {school.name}. The school will review your application and contact you soon.
              {emailSent && ' A confirmation email is on its way to the address you provided.'}
            </p>
          </div>
          <div className="auth-footer">
            <Link to="/">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container" style={{ maxWidth: 520 }}>
        <div className="auth-header">
          {school.logo_url && (
            <img src={school.logo_url} alt={school.name} style={{ maxHeight: 64, marginBottom: 12 }} />
          )}
          <h1>{school.name}</h1>
          <p>Admission Application Form</p>
          {school.require_application_fee && school.application_fee_amount && (
            <p style={{ fontSize: 13, color: '#9A3412' }}>
              A GHS {Number(school.application_fee_amount).toFixed(2)} application fee is required to complete this application.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="auth-form" data-testid="public-apply-form">
          {/* Honeypot — visually hidden via CSS (not type="hidden") so it stays in the
              tab order layout-wise for bots that skip aria/CSS-hidden fields less
              reliably than type="hidden" ones. Real users never see or fill this. */}
          <div style={{ display: 'none' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.honeypot}
              onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Child's First Name</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Child's Last Name</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Other Names</label>
            <input
              type="text"
              value={formData.other_names}
              onChange={(e) => setFormData({ ...formData, other_names: e.target.value })}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              disabled={submitting}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="form-group">
            <label>Parent/Guardian Name</label>
            <input
              type="text"
              value={formData.guardian_name}
              onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Relationship to Child</label>
            <input
              type="text"
              value={formData.guardian_relationship}
              onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })}
              placeholder="e.g., Mother, Father, Guardian"
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={formData.guardian_phone}
              onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
              required
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Email {school.require_application_fee ? '(required to receive a payment receipt)' : '(optional)'}</label>
            <input
              type="email"
              value={formData.guardian_email}
              onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Notes (e.g., class/grade applying for)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label>Supporting Documents (optional)</label>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
              Birth certificate, previous report card, or photo. PDF, JPEG or PNG, up to 5 MB each.
            </p>
            {documents.map((doc, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <select
                  value={doc.documentType}
                  onChange={(e) => updateDocumentSlot(index, { documentType: e.target.value })}
                  disabled={submitting}
                  style={{ flex: '0 0 auto', width: 170 }}
                >
                  {DOCUMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(e) => updateDocumentSlot(index, { file: e.target.files?.[0] || null })}
                  disabled={submitting}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => removeDocumentSlot(index)}
                  disabled={submitting}
                  style={{ padding: '4px 10px' }}
                >
                  Remove
                </button>
              </div>
            ))}
            {documents.length < MAX_DOCUMENTS && (
              <button type="button" className="btn btn-secondary" onClick={addDocumentSlot} disabled={submitting}>
                + Add a document
              </button>
            )}
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ApplyPage;
